import moment from 'moment'

// Mirrors server/src/helpers/scheduling/eventShiftMassUpdate.js
export const MASS_UPDATE_MAX_SHIFTS = 500

export const isShiftCovered = (shift) =>
  !!shift?.assignedTo ||
  !!shift?.assignedSubcontractor ||
  !!shift?.assignedToSubcontractor

export const shiftLockReason = (shift) =>
  shift?.isPaid
    ? 'Paid'
    : shift?.isVerified
      ? 'Verified'
      : shift?.staffFeedback
        ? 'Completed'
        : null

export const dayKey = (shift) => moment(shift.start).format('YYYY-MM-DD')

// "07:00–19:00" - groups shifts that run to the same pattern
export const patternKey = (shift) =>
  `${moment(shift.start).format('HH:mm')}–${moment(shift.end).format('HH:mm')}`

export const STATUS_FILTERS = [
  { value: 'all', label: 'All shifts' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'bookable', label: 'Bookable' },
  { value: 'notBookable', label: 'Not bookable' },
]

export const emptyFilters = () => ({
  days: [],
  roles: [],
  eventRoles: [],
  patterns: [],
  status: 'all',
})

const matchesStatus = (shift, status) => {
  switch (status) {
    case 'unassigned':
      return !isShiftCovered(shift)
    case 'assigned':
      return isShiftCovered(shift)
    case 'bookable':
      return !!shift.staffCanBook
    case 'notBookable':
      return !shift.staffCanBook
    default:
      return true
  }
}

// An empty list on any filter means "any"
export const filterShifts = (shifts, filters) =>
  shifts.filter(
    (shift) =>
      (!filters.days.length || filters.days.includes(dayKey(shift))) &&
      (!filters.roles.length || filters.roles.includes(shift.clinicalRole)) &&
      (!filters.eventRoles.length ||
        filters.eventRoles.includes(shift.eventAssignment || '')) &&
      (!filters.patterns.length ||
        filters.patterns.includes(patternKey(shift))) &&
      matchesStatus(shift, filters.status),
  )

// Distinct values for each filter's chips, with counts, in a stable order
export const getFilterOptions = (shifts) => {
  const count = (keyOf) => {
    const counts = new Map()
    shifts.forEach((shift) => {
      const key = keyOf(shift)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    })
    return [...counts.entries()]
      .map(([value, total]) => ({ value, total }))
      .sort((a, b) => String(a.value).localeCompare(String(b.value)))
  }
  return {
    days: count(dayKey),
    roles: count((shift) => shift.clinicalRole || ''),
    eventRoles: count((shift) => shift.eventAssignment || ''),
    patterns: count(patternKey),
  }
}

export const emptyChanges = () => ({
  times: {
    mode: 'none', // none | set | move
    // "Set": each is optional - blank keeps the shift's own value
    startDate: '', // YYYY-MM-DD
    start: '', // HH:mm
    endDate: '',
    end: '',
    minutes: 30,
    direction: 'later',
    target: 'both', // both | start | end
  },
  role: { on: false, value: '', applyDefaultRate: true },
  pay: { on: false, rate: '', hourly: true },
  eventRole: { on: false, value: '' },
  bookable: { on: false, value: true },
  note: { on: false, mode: 'append', text: '' },
  bonus: { on: false, mode: 'add', label: '', amount: '' },
})

// `day`'s calendar date at `hhmm` (or at `fallback`'s time of day)
const atTime = (day, hhmm, fallback) => {
  const [hour, minute] = hhmm
    ? hhmm.split(':').map(Number)
    : [moment(fallback).hour(), moment(fallback).minute()]
  return moment(day).clone().hour(hour).minute(minute).second(0).millisecond(0)
}

const isSetTimesEmpty = (times) =>
  !times.startDate && !times.start && !times.endDate && !times.end

// New {start, end} for a shift, or null when times aren't being changed.
// "Set" works in local wall-clock time and every field is optional: a blank
// date or time keeps the shift's own. A blank end date keeps the shift's
// existing day gap from its (new) start date, so overnight shifts stay
// overnight when only the start date moves.
export const computeShiftTimes = (shift, times) => {
  if (!times || times.mode === 'none') return null
  const start = moment(shift.start)
  const end = moment(shift.end)

  if (times.mode === 'set') {
    if (isSetTimesEmpty(times)) return null
    const startDay = times.startDate
      ? moment(times.startDate, 'YYYY-MM-DD')
      : start.clone().startOf('day')
    const dayGap = end
      .clone()
      .startOf('day')
      .diff(start.clone().startOf('day'), 'days')
    const endDay = times.endDate
      ? moment(times.endDate, 'YYYY-MM-DD')
      : startDay.clone().add(dayGap, 'days')
    return {
      start: atTime(startDay, times.start, start),
      end: atTime(endDay, times.end, end),
    }
  }

  const minutes =
    (Number(times.minutes) || 0) * (times.direction === 'earlier' ? -1 : 1)
  if (!minutes) return null
  return {
    start:
      times.target === 'end' ? start.clone() : start.clone().add(minutes, 'minutes'),
    end:
      times.target === 'start' ? end.clone() : end.clone().add(minutes, 'minutes'),
  }
}

export const endsBeforeStart = (nextTimes) =>
  !!nextTimes && !nextTimes.end.isAfter(nextTimes.start)

// The `changes` body for the server (times go separately, per shift)
export const buildChangesPayload = (changes, defaultPayRates = {}) => {
  const payload = {}
  if (changes.role.on && changes.role.value) {
    payload.clinicalRole = changes.role.value
    const defaultRate = defaultPayRates?.[changes.role.value]
    if (
      !changes.pay.on &&
      changes.role.applyDefaultRate &&
      defaultRate !== undefined &&
      defaultRate !== null &&
      defaultRate !== ''
    ) {
      payload.payRate = Number(defaultRate)
      payload.hourlyRate = true
    }
  }
  if (changes.pay.on) {
    payload.payRate = changes.pay.rate
    payload.hourlyRate = !!changes.pay.hourly
  }
  if (changes.eventRole.on) payload.eventAssignment = changes.eventRole.value ?? ''
  if (changes.bookable.on) payload.staffCanBook = !!changes.bookable.value
  if (changes.note.on) {
    payload.shiftNote = { mode: changes.note.mode, text: changes.note.text }
  }
  if (changes.bonus.on) {
    payload.bonuses =
      changes.bonus.mode === 'clear'
        ? { mode: 'clear' }
        : {
            mode: 'add',
            bonus: { label: changes.bonus.label, amount: changes.bonus.amount },
          }
  }
  return payload
}

// Problems with the change form itself (not with any particular shift)
export const getChangesError = (changes) => {
  const { times } = changes
  if (times.mode === 'set' && isSetTimesEmpty(times)) {
    return 'Enter at least one new date or time'
  }
  if (times.mode === 'move' && !(Number(times.minutes) > 0)) {
    return 'Enter how many minutes to move the shifts by'
  }
  if (changes.role.on && !changes.role.value) return 'Choose the new role'
  if (
    changes.pay.on &&
    (changes.pay.rate === '' || !(Number(changes.pay.rate) >= 0))
  ) {
    return 'Enter a pay rate of zero or more'
  }
  if (changes.note.on && changes.note.mode !== 'clear' && !changes.note.text.trim()) {
    return 'Enter the note text'
  }
  if (
    changes.bonus.on &&
    changes.bonus.mode === 'add' &&
    !(Number(changes.bonus.amount) > 0)
  ) {
    return 'Enter a bonus amount greater than zero'
  }
  return null
}

export const hasAnyChange = (changes) =>
  changes.times.mode !== 'none' ||
  changes.role.on ||
  changes.pay.on ||
  changes.eventRole.on ||
  changes.bookable.on ||
  changes.note.on ||
  changes.bonus.on

// Labels of what would change on one shift (for the preview), plus its new
// times. Mirrors buildShiftMassUpdate on the server.
export const previewShift = (shift, payload, nextTimes) => {
  const changed = []
  if (nextTimes) {
    if (!nextTimes.start.isSame(shift.start) || !nextTimes.end.isSame(shift.end)) {
      changed.push('times')
    }
  }
  if (payload.clinicalRole !== undefined && payload.clinicalRole !== shift.clinicalRole) {
    changed.push('role')
  }
  if (
    (payload.payRate !== undefined && Number(payload.payRate) !== Number(shift.payRate)) ||
    (payload.hourlyRate !== undefined && payload.hourlyRate !== !!shift.hourlyRate)
  ) {
    changed.push('pay')
  }
  if (
    payload.eventAssignment !== undefined &&
    payload.eventAssignment.trim() !== (shift.eventAssignment ?? '')
  ) {
    changed.push('event role')
  }
  if (
    payload.staffCanBook !== undefined &&
    payload.staffCanBook !== !!shift.staffCanBook
  ) {
    changed.push('bookable')
  }
  if (payload.shiftNote) {
    const current = shift.shiftNote ?? ''
    const { mode, text } = payload.shiftNote
    if (
      (mode === 'clear' && current) ||
      (mode === 'replace' && text.trim() !== current) ||
      (mode === 'append' && text.trim())
    ) {
      changed.push('notes')
    }
  }
  if (payload.bonuses) {
    if (payload.bonuses.mode === 'add' || (shift.bonuses ?? []).length) {
      changed.push('bonuses')
    }
  }
  return changed
}

export const buildTimesPayload = (shifts, times) =>
  Object.fromEntries(
    shifts
      .map((shift) => [shift._id, computeShiftTimes(shift, times)])
      .filter(([, next]) => next)
      .map(([id, next]) => [
        id,
        { start: next.start.toISOString(), end: next.end.toISOString() },
      ]),
  )
